import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BlogService from "services/BlogService";

const initialState = {
  blogs: [],
  currentBlog: null,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
};

// --- ASYNC THUNKS ---

export const getBlogs = createAsyncThunk(
  "blogs/getAll",
  async ({ page = 1, searchValue = {} }, { rejectWithValue, dispatch }) => {
    try {
      // Set pagination state first
      dispatch(setCurrentPage(page));
      const response = await BlogService.getAllBlogs(searchValue, page);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Erreur lors de la récupération des articles"
      );
    }
  }
);

export const getBlogById = createAsyncThunk(
  "blogs/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await BlogService.getBlogById(id);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Erreur lors de la récupération de l'article"
      );
    }
  }
);

export const createBlog = createAsyncThunk(
  "blogs/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BlogService.createBlog(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur lors de la création");
    }
  }
);

export const updateBlog = createAsyncThunk(
  "blogs/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await BlogService.updateBlogById(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur lors de la modification");
    }
  }
);

export const deleteBlog = createAsyncThunk(
  "blogs/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await BlogService.deleteBlogById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur lors de la suppression");
    }
  }
);

// --- SLICE ---
const blogSlice = createSlice({
  name: "blogs",
  initialState,
  reducers: {
    showLoading: (state) => { state.loading = true; },
    hideLoading: (state) => { state.loading = false; },
    hideError: (state) => { state.error = null; },
    setCurrentPage: (state, action) => { state.currentPage = action.payload; },
    clearStates: (state) => {
      state.blogs = [];
      state.totalItems = 0;
      state.totalPages = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBlogs.fulfilled, (state, action) => {
        state.loading = false;
        
        // Fix: Determine if payload is the array itself or contains a data property
        const result = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
        
        state.blogs = result;
        state.totalItems = action.payload?.total || result.length || 0;
        state.totalPages = action.payload?.totalPages || 1;
      })
      .addCase(getBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // GET BY ID
      .addCase(getBlogById.fulfilled, (state, action) => {
        state.currentBlog = action.payload;
        state.loading = false;
      })

      // Use matchers for global loading states
      .addMatcher(
        (action) => action.type.endsWith("/pending") && action.type.startsWith("blogs/"),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/fulfilled") && action.type.startsWith("blogs/"),
        (state) => {
          state.loading = false;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && action.type.startsWith("blogs/"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export default blogSlice.reducer;

export const {
  showLoading,
  hideLoading,
  hideError,
  setCurrentPage,
  clearStates,
} = blogSlice.actions;